import { Link } from '@tanstack/react-router';
import { Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { returnableLines } from '../schemas/return-rules';

import { OpenReturnDialog } from './open-return-dialog';
import { ReturnResolutionLabel, ReturnStatusBadge } from './return-status-badge';

/** Returns of one order with a way to open another while shipped units remain returnable. */
export function OrderReturnsCard({ storeId, order }: { storeId: string; order: Order }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const [opening, setOpening] = useState(false);
  const titles = new Map(order.lines.map((line) => [line.id, line.title]));
  const canOpen = order.status !== 'cancelled' && returnableLines(order).length > 0;

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('returns.title')}</CardTitle>
        {canOpen ? (
          <Can permission="returns.write">
            <CardAction>
              <Button variant="outline" size="sm" onClick={() => setOpening(true)}>
                <Undo2 aria-hidden />
                {t('returns.openAction')}
              </Button>
            </CardAction>
          </Can>
        ) : null}
      </CardHeader>
      <CardContent>
        {order.returns.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {canOpen ? t('returns.empty') : t('returns.nothingShipped')}
          </p>
        ) : (
          <ul className="flex flex-col gap-3 text-sm" aria-label={t('returns.title')}>
            {order.returns.map((ret) => (
              <li key={ret.id} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to="/s/$storeId/returns/$returnId"
                    params={{ storeId, returnId: ret.id }}
                    className="font-mono font-medium hover:underline"
                  >
                    {ret.rmaNumber}
                  </Link>
                  <span className="flex items-center gap-2">
                    <ReturnStatusBadge status={ret.status} />
                    <span
                      className="text-muted-foreground text-xs"
                      title={format.utc(ret.requestedAt)}
                    >
                      {format.relative(ret.requestedAt)}
                    </span>
                  </span>
                </div>
                {ret.resolution ? (
                  <p className="text-muted-foreground text-xs">
                    <ReturnResolutionLabel resolution={ret.resolution} />
                  </p>
                ) : null}
                <ul className="text-muted-foreground text-xs">
                  {ret.lines.map((line) => (
                    <li key={line.orderLineId}>
                      {line.quantity} ×{' '}
                      {titles.get(line.orderLineId) ?? t('fulfillments.unknownLine')}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {opening ? (
        <OpenReturnDialog
          storeId={storeId}
          order={order}
          open
          onOpenChange={(open) => {
            if (!open) setOpening(false);
          }}
        />
      ) : null}
    </Card>
  );
}
